import { useEffect, useRef } from 'react';

const VISIBILITY_THRESHOLD = 0.25;

/**
 * Observes the returned element ref and calls `onVisible` once when it
 * is at least 50% visible. The callback is stored in a ref so identity
 * changes (analytics helpers, inline functions) do not rebuild the
 * observer or re-fire the event.
 *
 * @param {() => void} onVisible
 * @returns {React.RefObject<Element>}
 */
export const useObserveVisibility = (onVisible) => {
    const elementRef = useRef(null);
    const onVisibleRef = useRef(onVisible);
    const hasFiredRef = useRef(false);

    onVisibleRef.current = onVisible;

    useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            (entries, observerInstance) => {
                entries.forEach((entry) => {
                    if (
                        entry.intersectionRatio < VISIBILITY_THRESHOLD ||
                        hasFiredRef.current
                    )
                        return;

                    hasFiredRef.current = true;
                    onVisibleRef.current?.();
                    observerInstance.unobserve(entry.target);
                });
            },
            { threshold: VISIBILITY_THRESHOLD }
        );

        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    return elementRef;
};
